// Base compartilhada do módulo de Estações Hidrométricas.
//
// Extraído de EstacoesMapa.tsx em 2026-08-06, quando o arquivo passou de 1.900
// linhas com 17 declarações — e a ficha descritiva ANA (Fase 4) ainda soma um
// formulário de ~60 campos. Sem quebrar, chegaria a ~3.000.
//
// ⚠ Este arquivo é MOVIMENTO PURO: o código foi recortado do original, não
// reescrito. Reescrever de memória já ia introduzindo bugs — a primeira versão
// deste arquivo quebrava parseValorCelula("1.5") em 15, porque removia pontos
// sempre em vez de só quando há vírgula E ponto.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

// ── Tipos do módulo ───────────────────────────────────────────────────────────
export interface Estacao {
  // `tipos` é a verdade (multi); `tipo` segue como espelho de tipos[0] pros
  // leitores legados (QR público, portal, camada do mapa).
  id: string; codigo: string; nome: string; tipo: string; tipos?: string[]; corpo_hidrico: string | null;
  // COMO adquire (ortogonal a `tipos`): convencional | automatica | telemetrica.
  // É o que decide as abas do modal. `transmissao` só vale com 'telemetrica'.
  formas?: string[]; transmissao?: 'iot' | 'satelital' | 'api' | null;
  latitude: number | null; longitude: number | null;
  cliente_id: number | null; contrato_id: string | null;
  cota_zero: number | null; unidade: string; faixa_min: number | null; faixa_max: number | null;
  status: string; observacoes: string | null; token_hash: string | null;
  cliente_nome?: string | null; contrato_numero?: string | null; contrato_titulo?: string | null;
  contrato_status?: string | null; contrato_prazo_final?: string | null;
  // `ultima_leitura_em` é SÓ régua e `ultima_telemetria_em` é só sensor — de
  // propósito, porque "0 régua · 949.807 telemetria" conta uma história que a
  // soma apagaria. `ultimo_dado_em` é o maior dos dois, pra quem só quer saber
  // se a estação está viva.
  leituras_total?: number; ultima_leitura_em?: string | null; leituras?: Leitura[];
  telemetria_total?: number; ultima_telemetria_em?: string | null; ultimo_dado_em?: string | null;
  variaveis_n?: number; cadencia_seg?: number | null;
  tem_ficha?: boolean; tem_curva?: boolean; medicoes_vazao?: number;
  // Manchete do card. A de telemetria e filtrada por GRANDEZA no backend — sem
  // isso viria a variavel mais recente, que numa estacao completa e DirVento.
  ultimo_valor_manual?: number | null; ultimo_valor_unidade?: string | null;
  ultima_variavel?: string | null; ultimo_valor_tele?: number | null;
  // presença do modem no IoT Core (null = estação sem telemetria IoT / sem histórico)
  modem_conectado?: boolean | null; modem_mudou_em?: string | null;
  modem_motivo?: string | null; modem_quedas_24h?: number;
}

// disconnectReason do IoT Core em português (fallback: código cru)
export const MOTIVOS_MODEM: Record<string, string> = {
  MQTT_KEEP_ALIVE_TIMEOUT: 'parou de responder (keep-alive)',
  CONNECTION_LOST: 'conexão perdida (rede/energia)',
  CLIENT_INITIATED_DISCONNECT: 'desligamento normal pelo equipamento',
  SERVER_INITIATED_DISCONNECT: 'derrubado pelo servidor',
  DUPLICATE_CLIENTID: 'outro dispositivo conectou com o mesmo ID',
  AUTH_ERROR: 'falha de autenticação',
  MQTT_PROTOCOL_ERROR: 'erro de protocolo MQTT',
};
export const motivoModem = (m?: string | null) => (m && (MOTIVOS_MODEM[m] || m)) || '';
export interface Leitura {
  id: string; valor: number; valor_consistido: number | null; unidade: string; nivel_calculado: number | null;
  lido_em: string; registrado_em: string; fonte: string; leiturista_nome: string | null;
  fora_faixa: boolean; observacao: string | null;
}
export interface ClienteOpt { id: number; razao_social: string | null; nome_fantasia: string | null }
export interface ContratoOpt { id: string; numero: string | null; titulo: string | null }

// ── Molinetes — tipos ─────────────────────────────────────────────────────────
export interface FaixaCal { n_min: number | null; n_max: number | null; a: number | null; b: number | null }
export interface Molinete {
  id: number; codigo: string | null; marca: string | null; modelo: string | null;
  helice: string | null; numero_serie: string | null; faixas: FaixaCal[];
  unidade_velocidade: string; observacao: string | null; ativo: boolean;
  numero_calibracao?: string | null; data_calibracao?: string | null;
  laudo_s3_key?: string | null; laudo_nome?: string | null;
  validade_calibracao?: string | null; dias_para_vencer?: number | null;
}
export interface DestAlerta { id: number; email: string; nome: string | null; ativo: boolean; }


// ── Janelas de análise ────────────────────────────────────────────────────────
export const PRESETS = [
  { label: '7d', dias: 7 }, { label: '30d', dias: 30 }, { label: '90d', dias: 90 },
  { label: '1 ano', dias: 365 }, { label: 'Tudo', dias: 0 },
];

// ── Telemetria — resolução, agregação e cadência ──────────────────────────────
export const TELE_RES = [
  { value: 'auto', label: 'Auto' }, { value: 'nativa', label: 'Nativa' },
  { value: '15min', label: '15 min' }, { value: '1h', label: '1 h' },
  { value: '6h', label: '6 h' }, { value: '1d', label: '1 dia' }, { value: '1sem', label: '1 sem' },
];
export const TELE_AGG = [
  { value: 'media', label: 'Média' }, { value: 'mediana', label: 'Mediana' },
  { value: 'ultimo', label: 'Último' }, { value: 'primeiro', label: 'Primeiro' },
  { value: 'max', label: 'Máx' }, { value: 'min', label: 'Mín' }, { value: 'soma', label: 'Soma' },
];
export function aggDefault(v: string): string {
  if (/mm|chuva|precip|rain/i.test(v)) return 'soma';       // chuva: acumula no bucket
  if (/bat|tens|volt/i.test(v)) return 'ultimo';            // bateria: o último estado
  return 'media';
}
export function fmtCadencia(seg: number | null): string {
  if (!seg) return '—';
  if (seg < 90) return `${seg}s`;
  if (seg < 5400) return `${Math.round(seg / 60)} min`;
  if (seg < 172800) return `${(seg / 3600).toFixed(1)} h`;
  return `${(seg / 86400).toFixed(1)} d`;
}


// ── API e formatação ──────────────────────────────────────────────────────────

export async function api(action: string, extra: any = {}) {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...extra }, { headers: { Authorization: token } });
  return r.data?.body ? JSON.parse(r.data.body) : r.data;
}
export async function listarClientes(): Promise<ClienteOpt[]> {
  const token = await getToken();
  const r = await axios.get(`${API_URL}/comercial/clientes`, { headers: { Authorization: token } });
  const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
  return Array.isArray(d) ? d : [];
}
export const nf = (v: number | null | undefined, dig = 2) => (v == null || v === '' as any) ? '—' : Number(v).toFixed(dig);

export function estiloOsm(): any {
  return {
    version: 8,
    sources: { osm: { type: 'raster', tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png','https://b.tile.openstreetmap.org/{z}/{x}/{y}.png','https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' } },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}


// ── Ajuste da curva-chave ─────────────────────────────────────────────────────
export function ajustarPotencia(pts: { cota: number; vazao: number }[]): { a: number; n: number; h0: number; r2: number } | null {
  const dados = pts.filter(p => p.vazao > 0 && Number.isFinite(p.cota) && Number.isFinite(p.vazao));
  if (dados.length < 3) return null;
  const cotas = dados.map(p => p.cota);
  const hmin = Math.min(...cotas), hmax = Math.max(...cotas);
  const range = Math.max(hmax - hmin, 0.01);
  let best: { a: number; n: number; h0: number; r2log: number } | null = null;
  const N = 400;
  for (let i = 0; i <= N; i++) {
    const h0 = (hmin - range * 2) + (i / N) * (range * 2 - 0.001);
    if (h0 >= hmin) continue;
    let sx = 0, sy = 0, sxx = 0, sxy = 0, m = 0, ok = true;
    for (const p of dados) {
      const dh = p.cota - h0;
      if (dh <= 0) { ok = false; break; }
      const x = Math.log(dh), y = Math.log(p.vazao);
      sx += x; sy += y; sxx += x * x; sxy += x * y; m++;
    }
    if (!ok || m < 3) continue;
    const denom = m * sxx - sx * sx;
    if (Math.abs(denom) < 1e-12) continue;
    const n = (m * sxy - sx * sy) / denom;
    const lnA = (sy - n * sx) / m;
    const ybar = sy / m;
    let ssTot = 0, ssRes = 0;
    for (const p of dados) {
      const y = Math.log(p.vazao), yhat = lnA + n * Math.log(p.cota - h0);
      ssTot += (y - ybar) ** 2; ssRes += (y - yhat) ** 2;
    }
    const r2log = ssTot > 0 ? 1 - ssRes / ssTot : 0;
    if (!best || r2log > best.r2log) best = { a: Math.exp(lnA), n, h0, r2log };
  }
  if (!best) return null;
  // R² no espaço linear (mais intuitivo pra apresentar)
  const qbar = dados.reduce((s, p) => s + p.vazao, 0) / dados.length;
  let ssTot = 0, ssRes = 0;
  for (const p of dados) {
    const qhat = best.a * Math.pow(p.cota - best.h0, best.n);
    ssTot += (p.vazao - qbar) ** 2; ssRes += (p.vazao - qhat) ** 2;
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return { a: best.a, n: best.n, h0: best.h0, r2 };
}


// ── Parsers de planilha ───────────────────────────────────────────────────────
export function parseLidoEmCelula(v: any): string | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v.toISOString();
  const s = String(v).trim();
  // DD/MM/YYYY [HH:mm[:ss]]
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    let yy = parseInt(m[3], 10); if (yy < 100) yy += 2000;
    const dt = new Date(yy, +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    return isNaN(dt.getTime()) ? null : dt.toISOString();
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString();
}
export function parseValorCelula(v: any): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (s.includes('.') && s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}
